import React, { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useGetQuery } from '../services/apiCall';
import { setDegreeData } from '../app/globals/slice/degreeSlice/DegreeSlice';
import { errorMessage } from '../components/atoms/toast/Toast';
import endpoints from '../api/endpoints';
import useOnlineStatus from '../app/hooks/useOnlineStatus';
import defaultImage from '../assets/images/defaultNews.png';
import HomePage from '../features/homePage/HomePage';
import Major from '../features/major/Major';
import ErrorPage from '../features/errorPage/ErrorPage';
import StudentProfile from '../features/studentProfile/StudentProfile';
import PageNotFound from '../features/PageNotFound/PageNotFound';
import PageHeader from '../components/templates/pageHeader/PageHeader';
import HistoryNew from '../features/aboutTheAcademy/outlets/historyNew/HistoryNew';
import Register from '../features/register/Register';
import CommingSoonPage from '../components/templates/CommingSoon/CommingSoonPage';
import RegistrationApp from '../features/registrationApplication/RegistrationApp';

import {
  AcademicCalendar,
  ResearchAndPublications,
  ResearchGuideline,
  SecurityAndLaw,
  Policies,
  Research,
  SubmitResearch,
  ResearchOportunity,
  TrainingCenter,
  AcademyInNumber,
  AcademyEcoMagazine,
  Overview,
  OrganizationalChart,
  Team,
  AcademicsOverview,
  CollegePostGraduateStudies,
  CollegeOfLaw,
  AcademicSupport,
  AcademicAdvising,
  NewsPage,
  AdmissionOverview,
  TuitionFees,
  UnderGraduate,
  Graduate,
  ScholarshipFinancial,
  Facilities,
  Alumni,
  Tour,
  StudentServices,
  DegreeAndPrograms,
  StudentLife,
} from './factory/LazyImports';
const FlipBookPDF = React.lazy(
  () => import('../features/pdf-view/FlipBookPDF')
);

import SingleNewsPage from '../components/templates/newsAndEventsTemplate/single-newsPage/SingleNewsPage';
import SingleMagazinePage from '../features/academyEcoMagazine/single-magazine/SingleMagazinePage';
import Layout from '../components/organism/layout/Layout';
import MediaCenter from '../components/templates/newsAndEventsTemplate/media-center/MediaCenter';
import useScrollToTop from '../app/hooks/useScrollToTop';
import Magazines from '../components/templates/academyEcoMagazineTemplate/magazines/Magazines';

const MainRouter = () => {
  useScrollToTop();
  const dispatch = useDispatch();
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();
  const fontSize = useSelector((state) => state.fontSize);

  const apiUrl = endpoints.degrees.getDegrees;
  const params = [100, 1];

  const { data, error } = useGetQuery({ apiUrl, params });

  useEffect(() => {
    if (data) {
      dispatch(setDegreeData(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    let multiplier;
    switch (fontSize) {
      case 'small':
        multiplier = 0.9;
        break;
      case 'medium':
        multiplier = 1;
        break;
      case 'large':
        multiplier = 1.05;
        break;
      case 'bigger':
        multiplier = 1.1;
        break;
      default:
        multiplier = 1;
    }

    document.body.style.setProperty('--font-size-multiplier', multiplier);
  }, [fontSize]);

  useEffect(() => {
    if (error) {
      errorMessage('error while loading data');
    }
  }, [error]);

  useEffect(() => {
    if (!isOnline) {
      navigate('/error');
    }
  }, [isOnline, navigate]);
  const header = { img: defaultImage };
  useEffect(() => {
    const hideTranslateLoader = () => {
      const loader = document.querySelector(
        '.VIpgJd-ZVi9od-aZ2wEe-wOHMyf-ti6hGc'
      );
      if (loader) {
        loader.style.display = 'none';
      }
    };

    const interval = setInterval(hideTranslateLoader, 1000);

    setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => clearInterval(interval);
  }, []);
  return (
    <Suspense fallback={<PageHeader data={header} isLoaded />}>
      <Routes>
        <Route element={<Layout />}>
          {/* ROOT */}
          <Route index element={<HomePage />} />

          {/* DEGREE PROGRAMS */}
          <Route path="degree-programs" element={<DegreeAndPrograms />} />
          <Route path="degree-programs/:type/:name/:id" element={<Major />} />

          {/* ABOUT */}
          <Route path="about">
            <Route index element={<Navigate replace to="overview" />} />
            <Route path="overview" element={<Overview />} />
            <Route path="history" element={<HistoryNew />} />
            {/* <Route path="graduation" element={<Graduation />} /> */}
            <Route path="leadership" element={<OrganizationalChart />} />
            <Route path="team" element={<Team />} />
          </Route>

          {/* ACADEMICS */}
          <Route path="academics">
            <Route index element={<Navigate replace to="overview" />} />
            <Route path="overview" element={<AcademicsOverview />} />
            <Route
              path="college-postgraduate"
              element={<CollegePostGraduateStudies />}
            />
            <Route path="college-law-police" element={<CollegeOfLaw />} />
            <Route path="academic-support" element={<AcademicSupport />} />
            <Route path="contact-us" element={<AcademicAdvising />} />
          </Route>

          {/* NEWS & EVENTS */}
          <Route path="news-and-events">
            <Route index element={<Navigate replace to="news" />} />
            <Route path="news" element={<NewsPage type="news" />} />
            <Route path="news/:id" element={<SingleNewsPage />} />
            <Route path="events" element={<NewsPage type="events" />} />
            <Route
              path="news-letter"
              element={<NewsPage type="news-letter" />}
            />{' '}
            <Route path="media" element={<NewsPage type="media" />} />
            <Route path="media-gallery" element={<MediaCenter />} />
          </Route>

          {/* ADMISSIONS */}
          <Route path="admissions">
            <Route index element={<Navigate replace to="overview" />} />
            <Route path="overview" element={<AdmissionOverview />} />
            <Route path="tuition-fees" element={<TuitionFees />} />
            <Route path="undergraduate" element={<UnderGraduate />} />
            <Route path="graduate" element={<Graduate />} />
            <Route
              path="scholarships-financial-aid"
              element={<ScholarshipFinancial />}
            />
          </Route>

          {/* STUDENTS AFFAIRS */}
          <Route path="students-affairs">
            <Route index element={<Navigate replace to="facilities" />} />
            <Route path="student-life" element={<StudentLife />} />
            <Route path="facilities" element={<Facilities />} />
            <Route path="alumni" element={<Alumni />} />
            <Route path="tour" element={<Tour />} />
            <Route path="student-services" element={<StudentServices />} />
            {/* <Route path="student-accessibility-services" element={<AccessibilityServices />} /> */}
          </Route>
          <Route path="academic-calendar" element={<AcademicCalendar />} />
          <Route path="academy-eco-magazine" element={<AcademyEcoMagazine />} />
          <Route path="magazines" element={<Magazines />} />

          <Route
            path="academy-eco-magazine/:id"
            element={<SingleMagazinePage />}
          />
          <Route path="security-and-law" element={<SecurityAndLaw />} />
          <Route
            path="academic-research-and-publications"
            element={<ResearchAndPublications />}
          />
          <Route path="policies" element={<Policies />} />
          <Route path="application/:type" element={<RegistrationApp />} />
          <Route path="research" element={<Research />} />
          <Route path="research/submit-research" element={<SubmitResearch />} />
          <Route
            path="research/oportunity/:id"
            element={<ResearchOportunity />}
          />
          <Route path="research/guideline" element={<ResearchGuideline />} />
          <Route path="training-center" element={<TrainingCenter />} />
          <Route path="academy-in-number" element={<AcademyInNumber />} />
          <Route path="student/profile/:id" element={<StudentProfile />} />
          <Route path="register" element={<Register />} />
          <Route path="coming-soon" element={<CommingSoonPage />} />

          {/* ERRORS */}
          <Route path="error" element={<ErrorPage />} />
        </Route>{' '}
        <Route path="pdf-view" element={<FlipBookPDF />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};

export default MainRouter;
